import { useTranslation } from 'react-i18next'

import { wwtMemo } from '@wwt/custom/wwt'

import { wwtPerformanceLog } from '@wwt/core/admin'
import { ReservationDto } from '@wwt/entities/api/search-response'
import { Offer } from '@wwt/entities/search-response'

import { OfferDescribedStatus } from '@/shared/ui/offer/OfferDescribedStatus.tsx'

import { useGuestInformationPayment } from '../../model/useGuestInformationPayment.ts'
import CustomerInformation from './CustomerInformation.tsx'
import { GuestInformationPaymentInformation } from './GuestInformationPaymentInformation.tsx'
import { GuestInformationReferenceInformation } from './GuestInformationReferenceInformation.tsx'
import { GuestInformationRoomGuests } from './GuestInformationRoomGuests.tsx'
import { GuestInformationSpecialWishes } from './GuestInformationSpecialWishes.tsx'

interface ReservationTabGuestInformationProps {
	offer: Offer
	reservation: ReservationDto
}

/**
 * Main block in ReservationTab
 *
 * @author Ivan Shchedrovskyi
 */
export const ReservationTabGuestInformation = wwtMemo(
	({ offer, reservation }: ReservationTabGuestInformationProps) => {
		wwtPerformanceLog('ReservationTabGuestInformation')

		const { t } = useTranslation('room-page', { keyPrefix: 'reservationTab' })

		const paymentStatus = useGuestInformationPayment(offer, t)
		return (
			<div className="col-span-12 lg:col-span-8">
				<section className="flex flex-col gap-y-6 rounded-md border-solid border-gray-200 px-5 py-0 sm:border sm:p-6 sm:shadow-md lg:p-4 xl:p-6">
					<OfferDescribedStatus
						status={offer.IDs.status}
						message={reservation.errorMessage}
					/>
					<h2 className="sr-only">{t('reservationInfo.title')}</h2>

					<article>
						<h2 className="ts-headline-4 mb-4">{t('customerInfo.title')}</h2>

						<CustomerInformation reservation={reservation} />
					</article>

					<article>
						<h2 className="ts-headline-4 mb-4">{t('guestInfo.title')}</h2>

						<article className="flex flex-col gap-y-4">
							<h2 className="ts-3">{offer.roomDynamic.name}</h2>

							<GuestInformationRoomGuests
								adultsList={reservation.adultsList}
								childrenList={reservation.childrenList}
								petList={reservation.petList}
							/>
						</article>
					</article>

					{reservation.comment && reservation.comment.length > 0 && (
						<GuestInformationSpecialWishes comment={reservation.comment} />
					)}

					<GuestInformationPaymentInformation
						source={t('paymentInformation.source')}
						status={paymentStatus}
					/>

					<GuestInformationReferenceInformation
						reservation={reservation}
						status={offer.IDs.status}
					/>

					<div className="w-[calc(100% + 2.5rem)] -ml-5 block h-[2px] bg-gray-200 sm:hidden" />
				</section>
			</div>
		)
	}
)
